import React from 'react';

import clsx from 'clsx';

import { AvatarBase, Circle } from './styles';

type Props = {
  alt: string;
  children?: any;
  className?: string;
  component?: any;
  imgProps?: Record<string, any>;
  sizes?: string;
  size?: number;
  src?: string;
  srcSet?: string;
  variant?: 'circle' | 'square';
  /**
   * Drawn when there is no image AND no children — a team with no logo, which is
   * what `TeamMeta` has always passed `TeamIcon` for.
   *
   * It was passed long before it was implemented, and `...rest` spread it onto
   * the styled `div`: React cannot put a component function in an attribute, so
   * every render of Settings logged "Invalid value for prop `fallback` on <div>
   * tag" while the avatar still drew nothing.
   */
  fallback?: React.ComponentType<Record<string, never>>;
};
export const classes = {
  root: 'NEXYAvatar',
  img: 'NEXYAvatarImg',
  circle: 'circle',
  square: 'square',
};
const themedComponent = {
  square: AvatarBase,
  circle: Circle,
};
const Avatar = React.forwardRef<Props, any>(function Avatar(props, ref) {
  const {
    alt,
    children: childrenProp,
    className,
    component: componentProp,
    imgProps,
    sizes,
    src,
    srcSet,
    variant = 'circle',
    size = 40,
    // DESTRUCTURED, which is half the fix: left in `...rest` it lands on the
    // styled `div` as an attribute and React warns on every render.
    fallback: Fallback,
    ...rest
  } = props;
  let children = null;
  const img = src || srcSet;

  if (img) {
    children = <img alt={alt} src={src} srcSet={srcSet} sizes={sizes} className={classes.img} {...imgProps} />;
  } else if (childrenProp) {
    children = childrenProp;
  } else if (Fallback) {
    // Last, so an explicit child still wins: the fallback is for having NOTHING
    // to draw, not for outranking what the call site handed over.
    children = <Fallback />;
  }

  let Component = themedComponent[variant] || AvatarBase;

  if (componentProp) {
    Component = componentProp;
  }

  return (
    <Component
      ref={ref}
      size={size}
      className={clsx(className, classes.root, {
        [classes.circle]: variant === 'circle',
        [classes.square]: variant === 'square',
      })}
      {...rest}
    >
      {children}
    </Component>
  );
});
export default Avatar;
